import React, { useState } from 'react';
import { FiChevronRight } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import Navbar from './Navbar';
import TestGraph from './TestGraph';
import './Reports.css';
import { secondsToHumanReadable } from '../Utils/timeUtils';

const PatientDetails = () => {
  const location = useLocation();
  const report = location.state?.report;
  const [expandedTests, setExpandedTests] = useState(new Set());
  const [chartType] = useState('smooth');
  const [showStats] = useState(true);
  const [statsHighlight] = useState('none');

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const hexToRgba = (hexColor, opacity = 1) => {
    if (!hexColor) return "rgba(0, 0, 0, 1)";
    if (typeof hexColor === "number") {
      const r = (hexColor >> 16) & 255;
      const g = (hexColor >> 8) & 255;
      const b = hexColor & 255;
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    const hex = hexColor.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const calculateStats = (dataPoints) => {
    if (!dataPoints || dataPoints.length === 0) return {};

    const sum = dataPoints.reduce((a, b) => a + b, 0);
    const avg = sum / dataPoints.length;
    const sortedPoints = [...dataPoints].sort((a, b) => a - b);
    const median = sortedPoints[Math.floor(dataPoints.length / 2)];
    const min = Math.min(...dataPoints);
    const max = Math.max(...dataPoints);

    const squareDiffs = dataPoints.map(value => {
      const diff = value - avg;
      return diff * diff;
    });
    const avgSquareDiff = squareDiffs.reduce((a, b) => a + b, 0) / squareDiffs.length;
    const stdDev = Math.sqrt(avgSquareDiff);

    const rateOfChange = [];
    for (let i = 1; i < dataPoints.length; i++) {
      rateOfChange.push(dataPoints[i] - dataPoints[i - 1]);
    }

    const avgRateOfChange = rateOfChange.reduce((a, b) => a + b, 0) / rateOfChange.length;

    const anomalies = dataPoints
      .map((point, index) => Math.abs(point - avg) > 2 * stdDev ? index : -1)
      .filter(index => index !== -1);

    return {
      mean: avg.toFixed(2),
      median: median.toFixed(2),
      min: min.toFixed(2),
      max: max.toFixed(2),
      stdDev: stdDev.toFixed(2),
      avgRateOfChange: avgRateOfChange.toFixed(2),
      anomalies,
    };
  };

  const toggleTestExpansion = (testIndex) => {
    setExpandedTests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(testIndex)) {
        newSet.delete(testIndex);
      } else {
        newSet.add(testIndex);
      }
      return newSet;
    });
  };

  const renderClinicalInfo = (test) => (
    <div className="clinical-info">
      <div className="clinical-info-item">
        <span className="clinical-info-label">Pre-Clinical Notes</span>
        <span className="clinical-info-value">{test.preClinicalNotes || "None"}</span>
      </div>
      <div className="clinical-info-item">
        <span className="clinical-info-label">Post-Clinical Notes</span>
        <span className="clinical-info-value">{test.postClinicalNotes || "None"}</span>
      </div>
      <div className="clinical-info-item">
        <span className="clinical-info-label">Leakage Volume</span>
        <span className="clinical-info-value">{test.leakageVolume || 0}</span>
      </div>
      <div className="clinical-info-item">
        <span className="clinical-info-label">Bladder Capacity</span>
        <span className="clinical-info-value">{test.bladderCapacity || "N/A"}</span>
      </div>
      <div className="clinical-info-item">
        <span className="clinical-info-label">Post Void Residue</span>
        <span className="clinical-info-value">{test.postVoidResidue || 0}</span>
      </div>
    </div>
  );

  const renderTestNotes = (test) => (
    <div className="notes-container">
      <h4>Test Notes</h4>
      {test.notes && test.notes.length > 0 ? (
        test.notes.map((note, index) => (
          <div
            key={index}
            className="note-item"
            style={{ borderLeft: `4px solid ${hexToRgba(note.color)}` }}
          >
            <p><strong>Time:</strong> {secondsToHumanReadable(note.timestamp)}</p>
            <p><strong>Note:</strong> {note.note}</p>
            <p><strong>Pressure:</strong> {note.pressure}</p>
          </div>
        ))
      ) : (
        <p>No notes available for this test</p>
      )}
    </div>
  );

  const renderSingleTest = (test) => {
    const stats = calculateStats(test.dataPoints);
    const pointRadiusArray = new Array(test.dataPoints.length).fill(0);
    const annotations = {};

    if (statsHighlight === "anomalies" && stats.anomalies) {
      stats.anomalies.forEach(index => {
        pointRadiusArray[index] = 5;
      });
    }

    return (
      <div className="single-test-container">
        <div className="test-header">
          <div className="test-header-info">
            <h3>Test Details</h3>
            <span className="test-date">{formatDate(test.createdAt)}</span>
          </div>
        </div>
        
        <TestGraph
          selectedTest={test}
          chartType={chartType}
          showStats={showStats}
          statsHighlight={statsHighlight}
          stats={stats}
          annotations={annotations}
          pointRadiusArray={pointRadiusArray}
        />
        
        {renderClinicalInfo(test)}
        {renderTestNotes(test)}
      </div>
    );
  };

  const renderTestGrid = () => {
    if (!report.cmgTests || report.cmgTests.length === 0) {
      return (
        <div className="no-tests-message">
          <h3>No Tests Available</h3>
          <p>There are no CMG tests recorded for this patient.</p>
        </div>
      );
    }

    if (report.cmgTests.length === 1) {
      return renderSingleTest(report.cmgTests[0]);
    }

    return (
      <div className="test-grid">
        {report.cmgTests.map((test, index) => {
          const stats = calculateStats(test.dataPoints);
          const pointRadiusArray = new Array(test.dataPoints.length).fill(0);
          const annotations = {};
          const isExpanded = expandedTests.has(index);

          if (statsHighlight === "anomalies" && stats.anomalies) {
            stats.anomalies.forEach(idx => {
              pointRadiusArray[idx] = 5;
            });
          }

          return (
            <div key={index} className="test-grid-item">
              <div className="test-header">
                <div className="test-header-info">
                  <h3>Test #{index + 1}</h3>
                  <span className="test-date">{formatDate(test.createdAt)}</span>
                </div>
                <button
                  className={`expand-button ${isExpanded ? 'expanded' : ''}`}
                  onClick={() => toggleTestExpansion(index)}
                  aria-label={isExpanded ? 'Collapse test details' : 'Expand test details'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d={isExpanded ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
                  </svg>
                </button>
              </div>

              <TestGraph
                selectedTest={test}
                chartType={chartType}
                showStats={showStats}
                statsHighlight={statsHighlight}
                stats={stats}
                annotations={annotations}
                pointRadiusArray={pointRadiusArray}
              />

              <div className={`test-details ${isExpanded ? 'expanded' : ''}`}>
                {renderClinicalInfo(test)}
                {renderTestNotes(test)}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!report) {
    return (
      <div className="admin-layout">
        <Navbar />
        <main className="admin-main">
          <div className="error">Report not found</div>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <Navbar />
      <main className="admin-main">
        <div className="admin-header">
          <div className="admin-welcome-section">
            <div className="breadcrumb">
              <Link to="/dashboard">Admin Dashboard</Link>
              <FiChevronRight className="breadcrumb-separator" />
              <Link to="/dashboard/reports">Reports</Link>
              <FiChevronRight className="breadcrumb-separator" />
              <span>{report.name}</span>
            </div>
            <h1>{report.name}</h1>
            <p>Patient Report Details</p>
          </div>
        </div>

        <div className="reports-container">
          <div className="patient-details">
            <div className="info-row">
              <span className="info-label">ID:</span>
              <span className="info-value">{report.patientId}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Age:</span>
              <span className="info-value">{report.age || "N/A"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Sex:</span>
              <span className="info-value">{report.sex || "N/A"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Height:</span>
              <span className="info-value">{report.height || "N/A"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Weight:</span>
              <span className="info-value">{report.weight || "N/A"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Contact:</span>
              <span className="info-value">{report.contact || "N/A"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Marital Status:</span>
              <span className="info-value">{report.maritalStatus || "N/A"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Created:</span>
              <span className="info-value">{formatDate(report.createdAt)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Aadhaar:</span>
              <span className="info-value">{report.aadhaar || "N/A"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Doctor ID:</span>
              <span className="info-value">{report.doctorId || "N/A"}</span>
            </div>
          </div>

          <div className="medical-section">
            <h3>Medical History</h3>
            <p>{report.medicalHistory || "No medical history recorded"}</p>

            <h3>Symptoms</h3>
            {report.symptoms && report.symptoms.length > 0 ? (
              <ul>
                {report.symptoms.map((symptom, index) => (
                  <li key={index}>{symptom}</li>
                ))}
              </ul>
            ) : (
              <p>No symptoms recorded</p>
            )}
          </div>

          <h3>CMG Tests ({report.cmgTests ? report.cmgTests.length : 0})</h3>
          {renderTestGrid()}
        </div>
      </main>
    </div>
  );
};

export default PatientDetails;